import { cn } from "@/lib/utils";

export type Presence = "online" | "away" | "offline";

const presenceColor: Record<Presence, string> = {
  online: "bg-success",
  away: "bg-warning",
  offline: "bg-muted",
};

export function Avatar({
  name,
  className,
  presence,
  decorative = false,
}: {
  name: string;
  className?: string;
  presence?: Presence;
  /** Beside the name it stands for: hidden from assistive tech, so the name is not read twice. */
  decorative?: boolean;
}) {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <span
      data-slot="avatar"
      title={decorative ? undefined : name}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : presence ? `${name}, ${presence}` : name}
      aria-hidden={decorative || undefined}
      className={cn(
        "relative inline-grid size-9 place-items-center rounded-pill border border-line-subtle bg-surface-muted text-xs font-medium text-foreground",
        className,
      )}
    >
      {initials}
      {presence ? (
        <span
          className={cn("absolute right-0 bottom-0 size-2.5 rounded-pill ring-2 ring-[var(--background)]", presenceColor[presence])}
          aria-hidden
        />
      ) : null}
    </span>
  );
}

export function AvatarGroup({ names, max = 4 }: { names: string[]; max?: number }) {
  const shown = names.slice(0, max);
  const extra = names.length - shown.length;
  return (
    <div data-slot="avatar-group" className="flex items-center" aria-label={names.join(", ")}>
      {shown.map((name, index) => (
        <Avatar key={name} name={name} className={cn("ring-2 ring-[var(--background)]", index > 0 && "-ml-2")} />
      ))}
      {extra > 0 ? (
        <span className="-ml-2 inline-grid size-9 place-items-center rounded-pill bg-surface-muted text-xs text-secondary ring-2 ring-[var(--background)]">
          +{extra}
        </span>
      ) : null}
    </div>
  );
}
